import { useCallback, useState } from 'react';

import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import useSession from '@/utils/hooks/useSession';
import { Typography } from '@mui/material';
import { navSectionClasses } from '../classes';
import { navSectionCssVars } from '../css-vars';
import { NavLi, NavUl, Subheader } from '../styles';
import { NavList } from './nav-list';

// ----------------------------------------------------------------------

export function NavSectionVertical({
  sx,
  data,
  render,
  slotProps,
  enabledRootRedirect,
  cssVars: overridesVars,
}) {
  const theme = useTheme();

  const cssVars = {
    ...navSectionCssVars.vertical(theme),
    ...overridesVars,
  };

  return (
    <Stack
      component='nav'
      className={navSectionClasses.vertical.root}
      sx={{ ...cssVars, ...sx }}
    >
      <NavUl sx={{ flex: '1 1 auto', gap: 'var(--nav-item-gap)' }}>
        {data.map((navItem) => {
          if (navItem.items === undefined) {
            return (
              <NavList
                key={navItem.label}
                data={navItem}
                render={render}
                depth={1}
                slotProps={slotProps}
                enabledRootRedirect={enabledRootRedirect}
              />
            );
          }
          return (
            <Group
              key={navItem.label}
              label={navItem.label}
              items={navItem.items}
              icon={navItem.icon}
              render={render}
              slotProps={slotProps}
              enabledRootRedirect={enabledRootRedirect}
            />
          );
        })}
      </NavUl>
    </Stack>
  );
}

// ----------------------------------------------------------------------

function Group({ items, render, label, icon, slotProps, enabledRootRedirect }) {
  const [open, setOpen] = useState(false);
  const { user, org } = useSession();

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  return (
    <NavLi>
      <Subheader
        data-title={label}
        open={open}
        onClick={handleToggle}
        sx={{
          ...slotProps?.subheader,
          color: 'text.secondary',
          fontWeight: 'medium',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          px: 0,
        }}
      >
        <span
          style={{
            margin: 'var(--nav-icon-margin)',
            width: 'var(--nav-icon-size)',
            height: 'var(--nav-icon-size)',
          }}
        >
          {icon}
        </span>
        <Typography
          variant='body1'
          sx={{
            flex: '1 1 auto',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
      </Subheader>

      <Collapse in={open}>
        <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
          {items.map((list) => (
            <NavList
              key={list.label}
              data={list}
              render={render}
              depth={1}
              slotProps={slotProps}
              enabledRootRedirect={enabledRootRedirect}
            />
          ))}
        </NavUl>
      </Collapse>
    </NavLi>
  );
}
